import { Link } from 'react-router-dom';
import { useGetSurveyResponseDetailsQuery } from '../../api/internal';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/Dialog';
import { Spinner } from '../../components/Spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/Table';

export interface SelectedBar {
  questionId: number;
  barIndex: number;
  answerValue: number;
  label: string;
  count: number;
  percentage: number;
  color: string;
}

interface SurveyResponseDetailDialogProps {
  selectedBar: SelectedBar;
  tenantIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  onClose: () => void;
}

export function SurveyResponseDetailDialog({
  selectedBar,
  tenantIds,
  dateFrom,
  dateTo,
  onClose
}: SurveyResponseDetailDialogProps) {
  const { data, isLoading, isError } = useGetSurveyResponseDetailsQuery({
    questionId: selectedBar.questionId,
    answerValue: selectedBar.answerValue,
    tenantIds,
    dateFrom,
    dateTo
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span
              className="inline-block h-3 w-3 shrink-0 rounded-full"
              style={{ backgroundColor: selectedBar.color }}
            />
            {selectedBar.label}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {selectedBar.count} {selectedBar.count === 1 ? 'response' : 'responses'} (
            {selectedBar.percentage.toFixed(2)}
            %)
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[300px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : isError ? (
            <div className="text-subtle py-4 text-center">Failed to load respondents. Please try again.</div>
          ) : !data?.respondents.length ? (
            <div className="text-subtle py-4 text-center">No respondents found.</div>
          ) : (
            <Table compact>
              <TableHeader className="bg-primary sticky top-0">
                <TableRow>
                  <TableHead>Patient</TableHead>
                  <TableHead>Clinic</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.respondents.map((respondent, index) => (
                  <TableRow key={index}>
                    <TableCell className="hover:underline">
                      <Link to={`/patients/${respondent.patientId}`}>{respondent.patientName}</Link>
                    </TableCell>
                    <TableCell>{respondent.clinicName}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
